// src/providers/TenantProvider.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useAuth,
  useUser,
  useOrganization,
  useOrganizationList
} from '@clerk/clerk-react'

type Ctx = {
  loading: boolean
  isSignedIn: boolean
  orgId: string | null
  orgSlug: string | null
  orgName: string | null
  org: any | null
  role: string | null
  superAdmin: boolean
  plan: string
  features: Record<string, unknown>
  hasRole: (...allow: string[]) => boolean
  on: (feature: string) => boolean
  limit: <T extends number = number>(name: string, fallback: T) => T
  getAuthHeader: () => Promise<any>
}

const TenantContext = createContext<Ctx | undefined>(undefined)

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const nav = useNavigate()
  const loc = useLocation()

  const {
    isLoaded: authLoaded,
    isSignedIn: signedInMaybe,
    getToken
  } = useAuth()
  const isSignedIn = !!signedInMaybe

  const { user, isLoaded: userLoaded } = useUser()
  const { organization, isLoaded: orgLoaded } = useOrganization()
  const {
    userMemberships,
    setActive,
    isLoaded: listLoaded
  } = useOrganizationList()

  const [autoTried, setAutoTried] = useState(false)
  const tryingRef = useRef(false)
  const allLoaded = authLoaded && userLoaded && orgLoaded && listLoaded

  useEffect(() => {
    if (!allLoaded || !isSignedIn) return
    if (organization || autoTried || tryingRef.current) return

    const memberships = userMemberships?.data ?? []
    if (memberships.length === 0) {
      setAutoTried(true)
      return
    }

    tryingRef.current = true
    const qs = new URLSearchParams(loc.search)
    const slugFromUrl = qs.get('org')?.toLowerCase()

    const target =
      (slugFromUrl &&
        memberships.find(m => m.organization.slug === slugFromUrl)) ||
      memberships[0]

    if (!target || !setActive) {
      setAutoTried(true)
      tryingRef.current = false
      return
    }

    setActive({ organization: target.organization.id })
      .catch(() => {})
      .finally(() => {
        setAutoTried(true)
        tryingRef.current = false
        if (slugFromUrl) {
          qs.delete('org')
          nav(
            { pathname: loc.pathname, search: qs.toString() },
            { replace: true }
          )
        }
      })
  }, [
    allLoaded,
    isSignedIn,
    organization,
    userMemberships,
    setActive,
    autoTried,
    loc.pathname,
    loc.search,
    nav
  ])

  const orgMeta = (organization?.publicMetadata ?? {}) as Record<
    string,
    unknown
  >
  const features = (orgMeta.features ?? {}) as Record<string, unknown>
  const plan = (orgMeta.plan as string) ?? 'free'

  const role =
    user?.organizationMemberships?.find(
      m => m.organization.id === organization?.id
    )?.role ?? null
  const superAdmin = Boolean(user?.publicMetadata?.super_admin)

  const hasRole = (...allow: string[]) => {
    if (superAdmin) return true
    return role ? allow.includes(role) : false
  }

  const on = (name: string) => Boolean(features[name])
  const limit = <T extends number = number>(name: string, fallback: T) =>
    Number(features[name] ?? fallback) as T

  // token helper with short in-memory cache
  const tokenCache = useRef<{ value: string | null; exp: number }>({
    value: null,
    exp: 0
  })
  const getAuthHeader = useCallback(async () => {
    const now = Date.now()
    if (tokenCache.current.value && tokenCache.current.exp > now) {
      return { Authorization: `Bearer ${tokenCache.current.value}` }
    }
    const token = await getToken().catch(() => null)
    if (!token) return {}
    tokenCache.current = { value: token, exp: now + 60_000 }
    return { Authorization: `Bearer ${token}` }
  }, [getToken])

  // Redirect if the user has 'pa-csr-1' or 'pa-csr-2' orgSlug
  useEffect(() => {
    if (organization?.slug === 'pa-csr-1' || organization?.slug === 'pa-csr-2') {
      nav('/leaderboard')
    }
  }, [organization?.slug, nav])

  const value = useMemo<Ctx>(
    () => ({
      loading: !allLoaded || (!organization && !autoTried),
      isSignedIn,
      orgId: organization?.id ?? null,
      orgSlug: organization?.slug ?? null,
      orgName: organization?.name ?? null,
      org: organization ?? null,
      role,
      superAdmin,
      plan,
      features,
      hasRole,
      on,
      limit,
      getAuthHeader
    }),
    [
      allLoaded,
      autoTried,
      isSignedIn,
      organization,
      role,
      superAdmin,
      plan,
      features,
      getAuthHeader
    ]
  )

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  )
}

export function useTenant() {
  const ctx = useContext(TenantContext)
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider')
  return ctx
}

export function useApi() {
  const { getAuthHeader } = useTenant()
  return useCallback(
    async (url: string, init: RequestInit = {}) => {
      const auth = await getAuthHeader()
      const res = await fetch(url, {
        ...init,
        headers: { ...(init.headers || {}), ...auth }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res
    },
    [getAuthHeader]
  )
}
